// Notifications, and they come from the PHONE.
//
// There is no sender, no server, no token and no credit anywhere in this file.
// Two things can put a notification on the lock screen:
//
//   the daily reminder  a repeating local schedule at the time in Settings, set
//                       on the device and kept by the OS. Nothing has to be
//                       running, and nothing is fetched to fire it.
//   a mispriced odd     a board the punter's OWN fetch just landed turned out
//                       to have an outlier on it. The app never fetches in
//                       order to notify — that would spend a credit to say
//                       something nobody asked for.
//
// PLUGIN ACCESS FOLLOWS `native.js`. `@capacitor/local-notifications` is in
// package.json so Gradle and SPM register it on the native side, and it is
// never imported here: the wrapper calls through to an implementation that only
// exists inside the native runtime, so importing it would bundle code into the
// web app that cannot run there. `plugin('LocalNotifications')` is present
// natively and null everywhere else, which is exactly the feature test this
// file wants. Read the long comment at the top of `native.js` before changing
// it — and it is also what lets a test hand this module a stub.
//
// EVERY ENTRY POINT FAILS SOFT AND IS A NO-OP WHEN IT SHOULD BE. Demo writes
// nothing and notifies nothing; a setting that is off silences its own
// notification here as well as at the call site; a web browser with no
// permission stays silent rather than asking on its own.

import { plugin } from './native.js';
import { isDemo } from './demo.js';
import { prefs } from './storage.js';
// Two leaves, and neither reaches for storage, a store or a network: the clock
// this app writes times with, and the question "is this id one of the three
// racing codes".
import { time } from '../lib/format.js';
import { isRacingSport } from '../lib/engine/teams.js';
// Pure, and a leaf. A break switches both settings off and cancels the schedule
// when it starts — this is the second lock: a reminder already on the device
// from before the break, or a board that lands while one is on, must not buzz.
import { onBreak } from '../lib/limits.js';

const PLUGIN = 'LocalNotifications';

/**
 * The daily reminder's id, fixed for the life of the app.
 *
 * Scheduling an id that already exists REPLACES it, which is the whole reason
 * the number is a constant: moving the time in Settings must move the one
 * reminder rather than add a second one, and it must do so on a device that has
 * been carrying the old one since a previous launch.
 */
export const DAILY_ID = 1001;

/** The reminder itself. One sentence, and it promises nothing about a price. */
export const DAILY_BODY = 'Today’s prices are in. Open Legwork to see the board';

/** How many outliers a single notification names before it stops listing. */
const NAMED = 3;

const local = () => plugin(PLUGIN);

/** The browser's own Notification constructor, where there is one. */
const webNotification = () =>
  (typeof window !== 'undefined' && typeof window.Notification === 'function'
    ? window.Notification
    : null);

/** The stored profile, read at call time — this module must not import the store. */
function profile() {
  try { return prefs.riskProfile ?? {}; } catch { return {}; }
}

/** Can this device show a notification at all? */
export function isSupported() {
  return !!local() || !!webNotification();
}

const WEB_STATE = { granted: 'granted', denied: 'denied' };

/** 'granted' | 'denied' | 'prompt' — 'prompt' meaning nobody has been asked yet. */
export async function permission() {
  const L = local();
  if (L?.checkPermissions) {
    try { return normalise((await L.checkPermissions())?.display); }
    catch { return 'denied'; }
  }
  const N = webNotification();
  if (!N) return 'denied';
  return WEB_STATE[N.permission] ?? 'prompt';
}

/** Ask. Answers the same three words `permission()` does. */
export async function requestPermission() {
  const L = local();
  if (L?.requestPermissions) {
    try { return normalise((await L.requestPermissions())?.display); }
    catch { return 'denied'; }
  }
  const N = webNotification();
  if (!N?.requestPermission) return 'denied';
  try { return WEB_STATE[await N.requestPermission()] ?? 'prompt'; }
  catch { return 'denied'; }
}

/** The plugin says granted/denied/prompt/prompt-with-rationale. Three words out. */
function normalise(display) {
  if (display === 'granted') return 'granted';
  if (display === 'denied') return 'denied';
  return 'prompt';
}

/** "4.3%" — the detector's edge as a punter reads it off the screen beside it. */
const pct = (edge) => `${(Number(edge) * 100).toFixed(1)}%`;

/**
 * "Ladbrokes 4.3% over on Carlton" — the same words the Matches row uses — and
 * on a race, "Neds 8.9% over on Southern Cross, Randwick R5 at 2:40 pm".
 *
 * A RACE NEEDS THREE MORE WORDS THAN A FIXTURE DOES. "Carlton" is a match: there
 * is one of them today and the punter knows which. "Southern Cross" is a runner
 * in one of forty races, its price is gone in twenty minutes, and a name with no
 * race and no time on it is a notification somebody cannot act on. The race and
 * the jump come off the leg itself (`race` and `kickoff`, added by
 * `racingOutliers`), and the time is the reader's own clock — `format.js`'s
 * `time` is the only place in the app a clock is written.
 *
 * "OVER" AND NOT "OVER THE EXCHANGE". Fair here is the rest of the market with
 * this book's vote taken out — every book that priced the field, the exchange
 * among them where the phone has a session and not otherwise (see
 * `outliersFor`). Naming one voter as the whole market would be a claim the
 * figure cannot carry, and the sports half of the same alert has said "over on"
 * since it shipped.
 */
const outlierPhrase = (o) => {
  const said = `${o.bookmakerName ?? o.bookmaker} ${pct(o.edge)} over on ${o.name}`;
  const where = [o.race, o.kickoff ? `at ${time(o.kickoff)}` : null].filter(Boolean).join(' ');
  return where ? `${said}, ${where}` : said;
};

/** Every one of them a runner? Then the alert belongs on the Racing tab. */
const allRacing = (list) => list.every((o) => isRacingSport(o?.sport));

/**
 * ONE notification for a whole board, never one per outlier.
 *
 * Three phones buzzing in a row for one fetch is how a useful alert becomes an
 * alert somebody switches off, so the list is summarised: the count in the
 * title, the longest three named in the body, and the rest implied by the
 * count. Tapping it opens Matches with the Mispriced chip already on, which is
 * the screen that shows all of them.
 */
export async function notifyOutliers(list) {
  const found = Array.isArray(list) ? list.filter(Boolean) : [];
  if (!found.length) return false;
  if (isDemo.value) return false;
  if (onBreak(profile())) return false;
  if (profile().notifyOutliers !== true) return false;

  const n = found.length;
  // "on today's card" when every one of them is a runner: a card and a board are
  // two different screens and the tap below is about to open one of them.
  const racing = allRacing(found);
  const title = `${n} mispriced ${n === 1 ? 'odd' : 'odds'} on today’s ${racing ? 'card' : 'board'}`;
  const body = found.slice(0, NAMED).map(outlierPhrase).join(' · ');
  // A MIXED ALERT OPENS MATCHES, because the board is where the app has always
  // sent this tap and one of the two screens has to win. An all-racing alert
  // opens the card with the same chip already on.
  const extra = { route: racing ? 'racing' : 'matches', chip: 'mispriced' };

  return deliver({ id: outlierId(), title, body, extra });
}

/**
 * A fresh id per alert, from the minute it fired.
 *
 * A fixed id would be tidy and wrong: the second alert of a day would quietly
 * replace an unread first one, and the punter would never know a price had been
 * flagged. Two alerts inside the same minute are the same fetch, which cannot
 * happen — `loadBoard` runs one at a time.
 */
const outlierId = () => 2000 + (Math.floor(Date.now() / 60_000) % 1000);

/**
 * The daily reminder, repeating at a local time.
 *
 * `schedule.on` is the plugin's repeating form: the hour and minute recur every
 * day on the device's own clock, so it follows the punter across a timezone and
 * a daylight-saving change without anything re-scheduling it.
 *
 * WEB CANNOT DO THIS. A page that is not open cannot post a notification, and a
 * service worker without a sender has nothing to wake it, so the web path is a
 * no-op rather than a reminder that only arrives while you are already looking
 * at the app. Answers false, and Settings still stores the time.
 */
/** Where the saved asks live (`src/lib/stores/ask.js`), read here for the reminder's words. */
export const SAVED_ASKS_KEY = 'askSaved';

/**
 * THE REMINDER SAYS WHAT IT IS FOR. With no saved asks it is the old line; with
 * some it names them — "Today's card is ready · Grand Final multis and 2 more" —
 * because a reminder about the punter's own question is worth opening and one
 * about "the board" is a nag. The words are fixed when the schedule is set, so
 * saving or deleting an ask reschedules it.
 */
export function dailyBodyFor(saved) {
  const names = (Array.isArray(saved) ? saved : []).map((s) => s?.name).filter((n) => typeof n === 'string' && n.trim());
  if (!names.length) return DAILY_BODY;
  const more = names.length > 1 ? ` and ${names.length - 1} more` : '';
  return `Today’s card is ready · ${names[0]}${more}`;
}

export async function scheduleDaily(hhmm) {
  if (isDemo.value) return false;
  if (onBreak(profile())) return false;
  if (profile().notifyDaily !== true) return false;
  const at = parseTime(hhmm);
  if (!at) return false;
  const L = local();
  if (!L?.schedule) return false;
  try {
    await L.schedule({
      notifications: [{
        id: DAILY_ID,
        title: 'Legwork',
        body: dailyBodyFor(prefs[SAVED_ASKS_KEY]),
        schedule: { on: { hour: at.hour, minute: at.minute }, allowWhileIdle: true },
      }],
    });
    return true;
  } catch { return false; }
}

/** Take the reminder off the device. Safe to call when nothing is scheduled. */
export async function cancelDaily() {
  const L = local();
  if (!L?.cancel) return false;
  try { await L.cancel({ notifications: [{ id: DAILY_ID }] }); return true; }
  catch { return false; }
}

/** 'HH:MM' → { hour, minute }, or null when it is not a time. */
export function parseTime(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm ?? '').trim());
  if (!m) return null;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (!(hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59)) return null;
  return { hour, minute };
}

// Handlers registered through `onTap`, for the web path's own click.
const webTaps = new Set();
const tapped = (extra) => { for (const h of webTaps) { try { h(extra); } catch { /* one handler */ } } };

/**
 * A tap on one of our notifications, with whatever `extra` it was sent with.
 *
 * The native event carries the whole notification; the handler only ever wants
 * the payload, so both paths hand over the same plain object and a caller
 * cannot tell which runtime it is in.
 */
export function onTap(handler) {
  if (typeof handler !== 'function') return;
  // The web has no listener registry of its own, so a click on a notification
  // this tab posted is routed back through the same handler.
  webTaps.add(handler);
  const L = local();
  if (!L?.addListener) return;
  try {
    L.addListener('localNotificationActionPerformed', (event) => {
      const extra = event?.notification?.extra;
      if (extra) handler(extra);
    });
  } catch { /* no listener: a tap just opens the app */ }
}

/**
 * Put one notification on the screen, by whichever path this runtime has.
 *
 * The web path is deliberately thin: it posts only where permission has already
 * been granted (asking inside a fetch nobody initiated is the behaviour
 * browsers went and blocked), and it is silent otherwise.
 */
async function deliver({ id, title, body, extra }) {
  const L = local();
  if (L?.schedule) {
    try {
      await L.schedule({ notifications: [{ id, title, body, extra }] });
      return true;
    } catch { return false; }
  }
  const N = webNotification();
  if (!N || N.permission !== 'granted') return false;
  try {
    const note = new N(title, { body, data: extra, tag: 'legwork-mispriced' });
    note.onclick = () => { tapped(extra); };
    return true;
  } catch { return false; }
}

